import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const REASONS = [
  { en: "Scam/fraud", fr: "Arnaque/fraude" },
  { en: "Harassment/abuse", fr: "Harcèlement/abus" },
  { en: "Spam", fr: "Pourriel" },
  { en: "Inappropriate content", fr: "Contenu inapproprié" },
  { en: "Other", fr: "Autre" },
];

const TXT = {
  en: { title: "Report", desc: "Reports are private and reviewed by CritterPosts moderators to help stop scams and abuse.", placeholder: "Add any details that will help us review (optional)…", cancel: "Cancel", submit: "Send report", pick: "Pick a reason", sent: "Report sent to our moderators. Thank you!" },
  fr: { title: "Signaler", desc: "Les signalements sont privés et examinés par les modérateurs de CritterPosts pour aider à stopper les arnaques et les abus.", placeholder: "Ajoutez des détails qui nous aideront (facultatif)…", cancel: "Annuler", submit: "Envoyer le signalement", pick: "Choisissez une raison", sent: "Signalement envoyé à nos modérateurs. Merci !" },
};

export const ReportUserDialog = ({ userId, userName, trigger }) => {
  const { user, setAuthOpen } = useAuth();
  const { lang } = useLanguage();
  const t = TXT[lang] || TXT.en;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const start = () => {
    if (!user) return setAuthOpen(true);
    setReason(""); setNote(""); setOpen(true);
  };

  const submit = async () => {
    if (!reason) return toast.error(t.pick);
    setLoading(true);
    try {
      await api.post(`/users/${userId}/report`, { reason, details: note.trim() });
      toast.success(t.sent);
      setOpen(false);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <span onClick={start}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="report-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#12234A] flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center"><Flag size={16} className="text-amber-500" /></span>
              {t.title} {userName || ""}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {t.desc}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2" data-testid="report-user-reasons">
            {REASONS.map((r) => (
              <button key={r.en} onClick={() => setReason(r.en)} data-testid={`report-user-reason-${r.en.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${reason === r.en ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-600"}`}>{lang === "fr" ? r.fr : r.en}</button>
            ))}
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="report-user-note" rows={3} maxLength={500} placeholder={t.placeholder} className="rounded-xl resize-none" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">{t.cancel}</Button>
            <Button onClick={submit} disabled={loading} data-testid="report-user-submit" className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white gap-1.5">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Flag size={15} />} {t.submit}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
